import { acceptHMRUpdate, defineStore } from 'pinia'
import _ from 'lodash'
import { integer } from 'vscode-languageserver-types'
import axios from '~/utils/api'
import { CompanyFetchStatus } from '~/utils/enums'
export type CompanyStoreState = {
  company: null | any,
  authorized: boolean,
  preview: boolean,
  redacting: boolean,
  templateName: null | string,
  previewData: null | any,
  redactingData: null | any,
  articles: null | any[],
  currentArticle: null | any,
  isDomain: boolean,
  initialTemplateData: null | any,
  templates: null | any[]
}

export const useCompanyStore = defineStore({
  id: 'company',
  state: (): CompanyStoreState => {
    return {
      company: null,
      authorized: false,
      preview: false,
      redacting: false,
      templateName: null,
      previewData: null,
      redactingData: null,
      articles: null,
      currentArticle: null,
      isDomain: false,
      initialTemplateData: null,
      templates: null
    }
  },
  getters: {
    currentArticleIndex(state): number {
      return state.articles?.length && state.currentArticle?.id
        ? state.articles.findIndex(
          article => article.id === state.currentArticle.id
        )
        : 0
    },
    articlePreviewId(state) {
      if (this.currentArticleIndex === 0) {
        return state.articles?.[state.articles.length - 1]?.id
      }

      return state.articles?.[this.currentArticleIndex - 1]?.id
    },
    articleNextId(state) {
      if (this.currentArticleIndex === state.articles.length - 1) {
        return state.articles?.[0]?.id
      }

      return state.articles?.[this.currentArticleIndex + 1]?.id
    },
    articlePrevious(state) {
      if (this.currentArticleIndex === 0) {
        return state.articles?.[state.articles.length - 1]
      }

      return state.articles?.[this.currentArticleIndex - 1]
    },
    articleNext(state) {
      if (this.currentArticleIndex === state.articles.length - 1) {
        return state.articles?.[0]
      }

      return state.articles?.[this.currentArticleIndex + 1]
    },
    havePublicArticles(state) {
      return (
        state.articles?.length &&
        state.articles.filter(a => a.isPublic).length
      )
    },
    templateDataCompare(state) {
      return _.isEqual(state.initialTemplateData, state.redactingData.templateData)
    }
  },
  actions: {
    async fetchById(id: number) {
      try {
        const { data } = await axios.get(`companies/${id}`)
        this.$patch({
          company: data.data
        })
        return CompanyFetchStatus.NOT_AUTH
      } catch (e) {
        return CompanyFetchStatus.FAILURE
      }
    },
    async fetchByDomain(domain: string) {
      try {
        const { data } = await axios.post('companies/company_by_domain', {
          domain
        })
        this.$patch({
          company: data.data
        })
        return CompanyFetchStatus.NOT_AUTH
      } catch (e) {
        return CompanyFetchStatus.FAILURE
      }
    },
    async fetchAuthenticated(id: number) {
      await axios.get('users/csrf-cookie')
      const { data } = await axios.get('companies/authenticated', {
        withCredentials: true
      })
      if (data.data.id !== id) {
        throw new Error('Can not be authorized')
      }
      this.$patch({
        company: data.data,
        authorized: true
      })
    },
    async fetchAuthenticatedOrById(id: number) {
      try {
        await this.fetchAuthenticated(id)
        return CompanyFetchStatus.AUTH
      } catch (e) {
        return await this.fetchById(id)
      }
    },
    async updateTemplateSettings(templateData: object) {
      await axios.put(`companies/${this.company.id}/templates`, templateData)
    },
    async uploadTemplatePicture(
      templatePath: string,
      templateId: number,
      file
    ) {
      const formData = new FormData()
      formData.append('photo', file.file)
      formData.append('templateId', `${templateId}`)
      formData.append('templatePath', templatePath)

      const { data } = await axios.post(
        '/companies/templates/upload_picture',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )

      this.$patch({
        company: data.data
      })
    },
    async uploadOfficePhotos(files) {
      const formData = new FormData()
      files.forEach(file => formData.append('photos[]', file))
      const { data } = await axios.post(
        `companies/${this.company.id}/office-photos`,
        formData
      )
      this.company.officePhotos.push(data.data)
    },
    async deletePictureByUrl(url: string) {
      if (!this.company?.officePhotos) {
        return
      }
      const photo = this.company?.officePhotos.find(
        officePhoto => officePhoto.photo.url === url
      )
      if (!photo) {
        return
      }

      await axios.delete(`companies/office-photo/${photo.id}`)
      this.company.officePhotos = this.company.officePhotos.filter(
        officePhoto => officePhoto.id !== photo.id
      )
    },
    async setRedactingTemplate(templateName: string) {
      await axios.post(`companies/templates/redacting_by_name/${templateName}`)
    },
    setPreview() {
      this.$patch({
        preview: true
      })
    },
    setRedacting() {
      this.$patch({
        redacting: true
      })
    },
    setTemplateName(templateName: string) {
      this.$patch({
        templateName
      })
    },
    setIsDomain(isDomain: boolean) {
      this.$patch({
        isDomain
      })
    },
    async fetchPreviewData() {
      const { data } = await axios.get(
        `companies/templates/preview/${this.templateName}`
      )

      this.$patch({
        previewData: data.data
      })
    },
    async fetchRedactingData() {
      const { data } = await axios.get(
        `companies/templates/company_template_settings/${this.templateName}`
      )

      this.$patch({
        redactingData: data?.data
      })

      this.initialTemplateData = Object.assign({}, this.redactingData.templateData)
    },
    async fetchArticlesByCompanyId(companyId: integer) {
      const { data } = await axios.get(`companies/${companyId}/articles`)

      this.$patch({
        articles: data?.data
      })
    },
    async fetchArticle(articleId: integer) {
      const { data } =
        this.redacting || this.preview
          ? await axios.get(`companies/articles/with_draft/${articleId}`)
          : await axios.get(`companies/articles/${articleId}`)

      this.$patch({
        currentArticle: data?.data
      })
    },
    async applyTemplate(companyId: integer, templateId: integer) {
      try {
        await axios.post(`companies/${companyId}/templates/apply`, {
          templateId
        })
        return CompanyFetchStatus.NOT_AUTH
      } catch (e) {
        return CompanyFetchStatus.FAILURE
      }
    },
    async fetchAllTemplates() {
      const { data } = await axios.get('companies/templates')

      this.$patch({
        templates: data?.data
      })
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCompanyStore, import.meta.hot))
}
